<template>
    <div v-if="showPrompt" :class="rootClass">
        <div :class="innerClass">
            <div :class="contentClass">
                <div :class="badgeClass" aria-hidden="true">
                    <span :class="badgeValueClass">{{ discountBadge }}</span>
                    <span :class="badgeLabelClass">Cupom ativo</span>
                </div>

                <div :class="textWrapClass">
                    <p :class="headlineClass">{{ headline }}</p>
                    <p :class="sublineClass">{{ subline }}</p>
                </div>
            </div>

            <div :class="actionsClass">
                <router-link :to="checkoutRoute" :class="primaryBtnClass">
                    {{ ctaLabel }}
                </router-link>
                <button type="button" :class="dismissBtnClass" @click="dismissPrompt">
                    {{ variant === 'banner' ? 'Depois' : 'Lembrar depois' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { usePendingRegistrationCoupon } from '@/composables/usePendingRegistrationCoupon';

const props = defineProps({
    variant: {
        type: String,
        default: 'banner',
        validator: (value) => ['banner', 'card'].includes(value),
    },
});

const {
    showPrompt,
    discountBadge,
    checkoutRoute,
    headline,
    subline,
    ctaLabel,
    dismissPrompt,
} = usePendingRegistrationCoupon({ surface: props.variant });

const isCard = computed(() => props.variant === 'card');

const rootClass = computed(() => (isCard.value ? 'coupon-prompt coupon-prompt--card' : 'coupon-prompt coupon-prompt--banner'));
const innerClass = computed(() => 'coupon-prompt__inner');
const contentClass = computed(() => 'coupon-prompt__content');
const badgeClass = computed(() => 'coupon-prompt__badge');
const badgeValueClass = computed(() => 'coupon-prompt__badge-value');
const badgeLabelClass = computed(() => 'coupon-prompt__badge-label');
const textWrapClass = computed(() => 'coupon-prompt__text');
const headlineClass = computed(() => 'coupon-prompt__headline');
const sublineClass = computed(() => 'coupon-prompt__subline');
const actionsClass = computed(() => 'coupon-prompt__actions');
const primaryBtnClass = computed(() => 'coupon-prompt__btn coupon-prompt__btn--primary');
const dismissBtnClass = computed(() => 'coupon-prompt__btn coupon-prompt__btn--ghost');
</script>

<style scoped>
.coupon-prompt--banner {
    background: linear-gradient(90deg, #faf5ff 0%, #eef2ff 55%, #f8fafc 100%);
    border-bottom: 1px solid rgba(99, 102, 241, 0.22);
}

.coupon-prompt--card {
    margin-bottom: 8px;
    border-radius: 14px;
    border: 1px solid rgba(99, 102, 241, 0.18);
    background: linear-gradient(135deg, #faf5ff 0%, #eef2ff 48%, #ffffff 100%);
    box-shadow: 0 10px 30px rgba(99, 102, 241, 0.08);
    overflow: hidden;
}

.coupon-prompt__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 18px;
}

.coupon-prompt--card .coupon-prompt__inner {
    align-items: center;
    padding: 14px 18px;
}

.coupon-prompt__content {
    display: flex;
    align-items: center;
    gap: 14px;
    flex: 1;
    min-width: 0;
}

.coupon-prompt__badge {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 72px;
    padding: 8px 10px;
    border-radius: 12px;
    background: linear-gradient(
        145deg,
        var(--brand-primary, #6366f1) 0%,
        var(--brand-accent, #7c3aed) 100%
    );
    box-shadow: 0 6px 16px rgba(var(--brand-primary-rgb, 99, 102, 241), 0.28);
}

.coupon-prompt--banner .coupon-prompt__badge {
    min-width: 64px;
    padding: 6px 8px;
    border-radius: 10px;
}

.coupon-prompt__badge-value {
    color: #fff;
    font-size: 0.92rem;
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: 0.02em;
}

.coupon-prompt--banner .coupon-prompt__badge-value {
    font-size: 0.78rem;
}

.coupon-prompt__badge-label {
    margin-top: 2px;
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.coupon-prompt__headline {
    margin: 0 0 4px;
    color: #1e1b4b;
    font-size: 0.92rem;
    font-weight: 600;
    line-height: 1.4;
}

.coupon-prompt--card .coupon-prompt__headline {
    font-size: 1.02rem;
    margin-bottom: 6px;
}

.coupon-prompt__subline {
    margin: 0;
    color: #5b21b6;
    font-size: 0.78rem;
    line-height: 1.45;
    opacity: 0.92;
}

.coupon-prompt--card .coupon-prompt__subline {
    font-size: 0.86rem;
}

.coupon-prompt__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.coupon-prompt--card .coupon-prompt__actions {
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    align-self: center;
    min-width: 168px;
}

.coupon-prompt__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 7px 14px;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
    text-decoration: none;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.coupon-prompt--card .coupon-prompt__btn {
    padding: 10px 16px;
    font-size: 0.84rem;
}

.coupon-prompt__btn--primary {
    color: #fff;
    background: linear-gradient(
        135deg,
        var(--brand-primary, #6366f1) 0%,
        var(--brand-accent, #7c3aed) 100%
    );
    box-shadow: 0 4px 14px rgba(var(--brand-primary-rgb, 99, 102, 241), 0.28);
}

.coupon-prompt__btn--primary:hover {
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(99, 102, 241, 0.34);
}

.coupon-prompt__btn--ghost {
    color: #6d28d9;
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid rgba(109, 40, 217, 0.18);
}

.coupon-prompt__btn--ghost:hover {
    color: #5b21b6;
    background: #fff;
}

@media (max-width: 768px) {
    .coupon-prompt__inner {
        flex-direction: column;
        align-items: stretch;
    }

    .coupon-prompt__content {
        align-items: flex-start;
    }

    .coupon-prompt__actions {
        width: 100%;
    }

    .coupon-prompt--card .coupon-prompt__actions {
        min-width: 0;
    }

    .coupon-prompt__btn {
        flex: 1;
    }
}
</style>

<style>
.dark .coupon-prompt--banner {
    background: linear-gradient(90deg, #1e1b4b 0%, #172554 100%);
    border-bottom-color: rgba(129, 140, 248, 0.28);
}

.dark .coupon-prompt--card {
    background: linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%);
    border-color: rgba(129, 140, 248, 0.24);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.28);
}

.dark .coupon-prompt__headline {
    color: #eef2ff;
}

.dark .coupon-prompt__subline {
    color: #c4b5fd;
}

.dark .coupon-prompt__btn--ghost {
    color: #ddd6fe;
    background: rgba(15, 23, 42, 0.72);
    border-color: rgba(167, 139, 250, 0.24);
}

.dark .coupon-prompt__btn--ghost:hover {
    color: #fff;
    background: rgba(30, 27, 75, 0.9);
}
</style>
